/**
 * Network-Aware Loading Service - Phase 4c: Quality Adjustment
 *
 * Detects connection speed and serves optimal quality:
 * - Fast (4G/LTE): Full resolution + MP4 animations
 * - Medium (3G): Medium resolution + videos
 * - Slow (2G/slow-2G): Thumbnails + text only
 * - Offline: Cached content only
 */

import React from 'react';
import { logger } from '../utils/logger';

export type NetworkSpeed = 'fast' | 'medium' | 'slow' | 'offline';

interface NetworkStatus {
  speed: NetworkSpeed;
  effectiveType?: string;
  downlink?: number; // Mbps
  rtt?: number; // ms
  isSaveData: boolean;
  isOnline: boolean;
}

export interface ImageQualityTier {
  speed: NetworkSpeed;
  resolution: 'full' | 'medium' | 'thumbnail';
  maxFileSize: number; // bytes
  description: string;
}

class NetworkAwareLoadingService {
  private currentStatus: NetworkStatus | null = null;
  private listeners = new Set<(status: NetworkStatus) => void>();

  // Quality tier configuration
  private readonly QUALITY_TIERS: Record<NetworkSpeed, ImageQualityTier> = {
    fast: {
      speed: 'fast',
      resolution: 'full',
      maxFileSize: 1024 * 1024, // 1MB
      description: '4G/LTE: Full quality'
    },
    medium: {
      speed: 'medium',
      resolution: 'medium',
      maxFileSize: 512 * 1024, // 512KB
      description: '3G: Medium quality'
    },
    slow: {
      speed: 'slow',
      resolution: 'thumbnail',
      maxFileSize: 50 * 1024, // 50KB
      description: '2G: Thumbnail only'
    },
    offline: {
      speed: 'offline',
      resolution: 'thumbnail',
      maxFileSize: 50 * 1024,
      description: 'Offline: Cached content'
    }
  };

  constructor() {
    this.detectNetworkStatus();
    this.setupNetworkListeners();
  }

  /**
   * Detect current network status
   */
  private detectNetworkStatus(): void {
    const status: NetworkStatus = {
      speed: this.determineNetworkSpeed(),
      isSaveData: this.checkSaveData(),
      isOnline: navigator.onLine
    };

    // Get detailed connection info if available
    if ('connection' in navigator) {
      const conn = (navigator as any).connection;
      status.effectiveType = conn?.effectiveType;
      status.downlink = conn?.downlink;
      status.rtt = conn?.rtt;
    }

    this.currentStatus = status;

    logger.debug('Network status detected:', {
      speed: status.speed,
      effectiveType: status.effectiveType,
      saveData: status.isSaveData,
      online: status.isOnline
    });
  }

  /**
   * Determine network speed from connection API
   */
  private determineNetworkSpeed(): NetworkSpeed {
    if (!navigator.onLine) return 'offline';

    if ('connection' in navigator) {
      const conn = (navigator as any).connection;
      const effectiveType = conn?.effectiveType;

      switch (effectiveType) {
        case '4g':
          return 'fast';
        case '3g':
          return 'medium';
        case '2g':
        case 'slow-2g':
          return 'slow';
        default:
          return 'medium'; // Default assumption
      }
    }

    // Fallback: assume medium speed if API not available
    return 'medium';
  }

  /**
   * Check if user has enabled "Save Data" mode
   */
  private checkSaveData(): boolean {
    if ('connection' in navigator) {
      return (navigator as any).connection?.saveData || false;
    }
    return false;
  }

  /**
   * Setup listeners for network changes
   */
  private setupNetworkListeners(): void {
    // Listen for online/offline events
    window.addEventListener('online', () => {
      logger.info('Network: Online');
      this.detectNetworkStatus();
      this.notifyListeners();
    });

    window.addEventListener('offline', () => {
      logger.info('Network: Offline');
      this.detectNetworkStatus();
      this.notifyListeners();
    });

    // Listen for connection type changes
    if ('connection' in navigator) {
      (navigator as any).connection.addEventListener('change', () => {
        logger.debug('Network: Connection type changed');
        this.detectNetworkStatus();
        this.notifyListeners();
      });
    }
  }

  /**
   * Notify all listeners of network status change
   */
  private notifyListeners(): void {
    if (this.currentStatus) {
      const listenerArray = Array.from(this.listeners);
      for (const listener of listenerArray) {
        listener(this.currentStatus);
      }
    }
  }

  /**
   * Subscribe to network status changes
   */
  subscribe(listener: (status: NetworkStatus) => void): () => void {
    this.listeners.add(listener);

    // Unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Get current network status
   */
  getStatus(): NetworkStatus {
    if (!this.currentStatus) {
      this.detectNetworkStatus();
    }
    return this.currentStatus!;
  }

  /**
   * Get quality tier for current network
   */
  getQualityTier(): ImageQualityTier {
    const status = this.getStatus();

    // If saveData enabled, always use slow tier
    if (status.isSaveData) {
      return this.QUALITY_TIERS.slow;
    }

    return this.QUALITY_TIERS[status.speed];
  }

  /**
   * Get appropriate image URL based on network speed
   */
  getImageUrl(
    fullResolutionUrl: string,
    mediumResolutionUrl?: string,
    thumbnailUrl?: string
  ): string {
    const tier = this.getQualityTier();

    switch (tier.resolution) {
      case 'full':
        return fullResolutionUrl;
      case 'medium':
        return mediumResolutionUrl || fullResolutionUrl;
      case 'thumbnail':
        return thumbnailUrl || mediumResolutionUrl || fullResolutionUrl;
      default:
        return fullResolutionUrl;
    }
  }

  /**
   * Determine whether to load video or fallback to image
   */
  shouldLoadVideo(): boolean {
    const tier = this.getQualityTier();

    // Only load videos on fast or medium connections
    return tier.speed === 'fast' || tier.speed === 'medium';
  }

  /**
   * Get video bitrate recommendation based on network
   */
  getVideoBitrate(): number {
    const tier = this.getQualityTier();

    switch (tier.speed) {
      case 'fast':
        return 5000; // 5 Mbps for 1080p
      case 'medium':
        return 2500; // 2.5 Mbps for 720p
      case 'slow':
      case 'offline':
        return 500; // 500 kbps for 360p
      default:
        return 2500;
    }
  }

  /**
   * Create network status badge for UI
   */
  getStatusBadge(): {
    label: string;
    color: string;
    icon: string;
  } {
    const status = this.getStatus();

    if (!status.isOnline) {
      return { label: 'Offline', color: 'red', icon: '📡' };
    }

    switch (status.speed) {
      case 'fast':
        return { label: '4G', color: 'green', icon: '📶' };
      case 'medium':
        return { label: '3G', color: 'yellow', icon: '📶' };
      case 'slow':
        return { label: '2G', color: 'orange', icon: '📡' };
      default:
        return { label: 'Online', color: 'blue', icon: '🌐' };
    }
  }

  /**
   * Force quality tier (for testing or user override)
   */
  forceQualityTier(tier: NetworkSpeed): void {
    if (this.currentStatus) {
      this.currentStatus.speed = tier;
      logger.warn(`Network quality tier forced to: ${tier}`);
      this.notifyListeners();
    }
  }

  /**
   * Reset quality tier to auto-detection
   */
  resetQualityTier(): void {
    this.detectNetworkStatus();
    this.notifyListeners();
  }
}

// Export singleton instance
export const networkAwareLoading = new NetworkAwareLoadingService();

/**
 * React hook for network awareness
 */
export function useNetworkAwareness() {
  const [status, setStatus] = React.useState(() => networkAwareLoading.getStatus());

  React.useEffect(() => {
    const unsubscribe = networkAwareLoading.subscribe((newStatus) => {
      setStatus(newStatus);
    });

    return unsubscribe;
  }, []);

  return {
    status,
    qualityTier: networkAwareLoading.getQualityTier(),
    shouldLoadVideo: networkAwareLoading.shouldLoadVideo(),
    getImageUrl: networkAwareLoading.getImageUrl.bind(networkAwareLoading),
    getStatusBadge: networkAwareLoading.getStatusBadge.bind(networkAwareLoading)
  };
}
